import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { removeToken } from './auth';
import Input from './components/Input';
import Button from './components/Button';
import { useToast } from './ToastContext';
import {
  getCurrentUser,
  updateUser,
  getGeminiKey,
  getGeminiModels,
  setGeminiKey,
  deleteGeminiKey,
  getPageSpeedKey,
  setPageSpeedKey,
  deletePageSpeedKey,
  testGeminiKey,
  getSemrushKey,
  setSemrushKey,
  deleteSemrushKey,
  getSemrushCredits,
  getGeminiUsage,
  resetGeminiUsage,
} from './api';
import Avatar from './components/Avatar';
import Card from './components/Card';
import Tab from './components/Tab';
import Loader from './components/Loader';
import Badge from './components/Badge';
import { clearAppCache } from './utils/clearAppCache';

export default function Profile() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'infos');
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    avatar: '',
    password: '',
    password2: '',
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { showToast } = useToast();

  // Ne garder que le bloc Clé Gemini branché sur le backend dans l'onglet 'apikeys'
  const [geminiConfigured, setGeminiConfigured] = useState(false);
  const [geminiShow, setGeminiShow] = useState(false);
  const [geminiLoading, setGeminiLoading] = useState(false);
  const [geminiInput, setGeminiInput] = useState('');
  const [geminiUsage, setGeminiUsage] = useState(null);
  const [geminiUsageResetting, setGeminiUsageResetting] = useState(false);

  // Clé PageSpeed globale
  const [pagespeedConfigured, setPagespeedConfigured] = useState(false);
  const [pagespeedShow, setPagespeedShow] = useState(false);
  const [pagespeedLoading, setPagespeedLoading] = useState(false);
  const [pagespeedInput, setPagespeedInput] = useState('');

  // Clé Semrush
  const [semrushConfigured, setSemrushConfigured] = useState(false);
  const [semrushShow, setSemrushShow] = useState(false);
  const [semrushLoading, setSemrushLoading] = useState(false);
  const [semrushInput, setSemrushInput] = useState('');
  const [semrushCredits, setSemrushCredits] = useState(null);

  // Charger les clés API à l'ouverture de l'onglet API keys
  useEffect(() => {
    if (activeTab === 'apikeys') {
      // Charger Gemini
      setGeminiLoading(true);
      getGeminiKey()
        .then(data => {
          setGeminiConfigured(!!data.is_configured);
          setGeminiInput(data.key || '');
          setGeminiLoading(false);
          if (!data.is_configured) setOpenDropdownGemini(true);
        })
        .catch(() => {
          setGeminiConfigured(false);
          setGeminiInput('');
          setGeminiLoading(false);
          setOpenDropdownGemini(true);
        });
      // Charger la consommation Gemini
      getGeminiUsage().then(setGeminiUsage).catch(() => setGeminiUsage(null));

      // Charger PageSpeed
      setPagespeedLoading(true);
      getPageSpeedKey()
        .then(data => {
          setPagespeedConfigured(!!data.is_configured);
          setPagespeedInput(data.key || '');
          setPagespeedLoading(false);
          if (!data.is_configured) setOpenDropdownPageSpeed(true);
        })
        .catch(() => {
          setPagespeedConfigured(false);
          setPagespeedInput('');
          setPagespeedLoading(false);
          setOpenDropdownPageSpeed(true);
        });

      // Charger Semrush
      setSemrushLoading(true);
      getSemrushKey()
        .then(data => {
          setSemrushConfigured(!!data.is_configured);
          setSemrushInput(data.key || '');
          setSemrushLoading(false);
          if (!data.is_configured) setOpenDropdownSemrush(true);
        })
        .catch(() => {
          setSemrushConfigured(false);
          setSemrushInput('');
          setSemrushLoading(false);
          setOpenDropdownSemrush(true);
        });

      // Charger le solde de crédits Semrush
      getSemrushCredits()
        .then(data => setSemrushCredits(data.credits))
        .catch(() => setSemrushCredits(null));
    }
  }, [activeTab]);

  // Enregistrer ou modifier la clé Gemini
  async function handleGeminiSave() {
    if (!geminiInput) return;
    setGeminiLoading(true);
    try {
      await setGeminiKey(geminiInput);
      setGeminiConfigured(true);
      showToast('Clé Gemini enregistrée !', 'success');
      window.dispatchEvent(new CustomEvent('api-keys-updated'));
    } catch (error) {
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setGeminiLoading(false);
    }
  }
  // Supprimer la clé Gemini
  async function handleGeminiDelete() {
    setGeminiLoading(true);
    try {
      await deleteGeminiKey();
      setGeminiConfigured(false);
      setGeminiInput('');
      setGeminiModelsList(null);
      showToast('Clé Gemini supprimée', 'success');
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setGeminiLoading(false);
    }
  }

  // Enregistrer ou modifier la clé PageSpeed
  async function handlePageSpeedSave() {
    if (!pagespeedInput) return;
    setPagespeedLoading(true);
    try {
      await setPageSpeedKey(pagespeedInput);
      setPagespeedConfigured(true);
      showToast('Clé Google PageSpeed enregistrée !', 'success');
      window.dispatchEvent(new CustomEvent('api-keys-updated'));
    } catch (error) {
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setPagespeedLoading(false);
    }
  }

  // Supprimer la clé PageSpeed
  async function handlePageSpeedDelete() {
    setPagespeedLoading(true);
    try {
      await deletePageSpeedKey();
      setPagespeedConfigured(false);
      setPagespeedInput('');
      showToast('Clé Google PageSpeed supprimée', 'success');
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setPagespeedLoading(false);
    }
  }

  // Enregistrer ou modifier la clé Semrush
  async function handleSemrushSave() {
    if (!semrushInput) return;
    setSemrushLoading(true);
    try {
      await setSemrushKey(semrushInput);
      setSemrushConfigured(true);
      showToast('Clé Semrush enregistrée !', 'success');
      window.dispatchEvent(new CustomEvent('api-keys-updated'));
    } catch (error) {
      showToast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSemrushLoading(false);
    }
  }

  // Supprimer la clé Semrush
  async function handleSemrushDelete() {
    setSemrushLoading(true);
    try {
      await deleteSemrushKey();
      setSemrushConfigured(false);
      setSemrushInput('');
      showToast('Clé Semrush supprimée', 'success');
    } catch (error) {
      showToast('Erreur lors de la suppression', 'error');
    } finally {
      setSemrushLoading(false);
    }
  }

  // Ajout pour test Gemini
  const [geminiPrompt, setGeminiPrompt] = useState('');
  const [geminiTestLoading, setGeminiTestLoading] = useState(false);
  const [geminiTestResult, setGeminiTestResult] = useState(null);
  const [geminiTestError, setGeminiTestError] = useState('');
  // Modèles disponibles pour la clé (version de compte)
  const [geminiModelsList, setGeminiModelsList] = useState(null);
  const [geminiModelsLoading, setGeminiModelsLoading] = useState(false);

  async function handleGeminiTest() {
    setGeminiTestLoading(true);
    setGeminiTestResult(null);
    setGeminiTestError('');
    try {
      // Envoyer la clé actuellement dans le champ : on teste celle affichée (nouvelle ou déjà enregistrée)
      const data = await testGeminiKey(
        geminiPrompt || 'Réponds simplement "OK" pour confirmer que la clé API fonctionne.',
        geminiInput || undefined
      );
      if (data.success) {
        setGeminiTestResult(data);
        showToast('Clé API Gemini valide et fonctionnelle', 'success');
      } else {
        setGeminiTestError(data.detail || data.message || 'Erreur Gemini');
        showToast(data.detail || data.message || 'Erreur lors du test de la clé API', 'error');
      }
    } catch (e) {
      const errorMessage = e.response?.data?.detail || e.response?.data?.message || e.message;
      setGeminiTestError('Erreur: ' + errorMessage);
      showToast(errorMessage || 'Erreur réseau lors du test', 'error');
    } finally {
      setGeminiTestLoading(false);
    }
  }

  async function handleFetchGeminiModels() {
    setGeminiModelsLoading(true);
    setGeminiModelsList(null);
    try {
      const data = await getGeminiModels();
      setGeminiModelsList(data.models || []);
    } catch (err) {
      showToast(err?.response?.data?.detail || 'Impossible de récupérer la liste des modèles', 'error');
    } finally {
      setGeminiModelsLoading(false);
    }
  }

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      try {
        const data = await getCurrentUser();
        setForm(f => ({ ...f, ...data, avatar: data.avatar || '' }));
      } catch (error) {
        console.error('Erreur chargement profil:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'avatar' && files) {
      setAvatarFile(files[0]);
    }
    setForm((prev) => ({ ...prev, [name]: files ? files[0].name : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccess(false);
    const formData = new FormData();
    formData.append('first_name', form.first_name);
    formData.append('last_name', form.last_name);
    if (avatarFile) formData.append('avatar', avatarFile);
    if (form.password) {
      if (form.password !== form.password2) {
        setErrors({ password2: 'Les mots de passe ne correspondent pas' });
        showToast('Les mots de passe ne correspondent pas', 'error');
        return;
      }
      formData.append('password', form.password);
    }
    setLoading(true);
    try {
      await updateUser(formData);
      setSuccess(true);
      setForm(f => ({ ...f, password: '', password2: '' }));
      showToast('Profil mis à jour !', 'success');
    } catch (error) {
      const errorData = error.response?.data || {};
      setErrors(errorData);
      showToast(errorData.detail || 'Erreur lors de la modification', 'error');
    } finally {
      setLoading(false);
    }
  };

  const [openDropdownGemini, setOpenDropdownGemini] = useState(false);
  const [openDropdownPageSpeed, setOpenDropdownPageSpeed] = useState(false);
  const [openDropdownSemrush, setOpenDropdownSemrush] = useState(false);

  /** Prochaine carte à mettre en avant (ordre : Gemini → PageSpeed → Semrush) */
  const nextCardToConfigure =
    !geminiConfigured ? 'gemini' :
    !pagespeedConfigured ? 'pagespeed' :
    !semrushConfigured ? 'semrush' :
    null;

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="w-full">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl mb-1">Mon Profil</h1>
            <p className="text-blue-gray-400">Gérez vos informations personnelles et vos clés API</p>
          </div>
          <Button onClick={handleLogout} variant="secondary">
            Se déconnecter
          </Button>
        </div>
        <Tab
          active={activeTab}
          onChange={tab => setActiveTab(tab)}
          className="mb-6"
          tabData={[
            {
              key: 'infos', label: 'Informations personnelles', icon: (
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
              )
            },
            {
              key: 'apikeys', label: 'Clés API', icon: (
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M7 14l5-5 5 5" /><path d="M12 19V9" /><circle cx="12" cy="12" r="10" /></svg>
              )
            },
          ]}
        />
        {activeTab === 'infos' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <Card className="lg:col-span-2">
              <h2 className="font-heading text-xl mb-4">Photo de profil</h2>
              <div className="flex items-center gap-6 mb-6">
                <div className="relative group">
                  <Avatar src={avatarFile ? URL.createObjectURL(avatarFile) : form.avatar} alt={form.first_name || form.email} size={80} className="border-2 border-blue-gray-100 shadow" />
                  <label htmlFor="avatar-upload" className="absolute bottom-0 right-0 bg-accent text-white rounded-full p-2 cursor-pointer shadow-lg opacity-90 hover:opacity-100 transition-opacity group-hover:scale-105 flex items-center justify-center" style={{ width: 40, height: 40 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h2l2-3h6l2 3h2a2 2 0 012 2v8a2 2 0 01-2 2H3a2 2 0 01-2-2V9a2 2 0 012-2zm9 4a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                    <input id="avatar-upload" type="file" name="avatar" accept="image/*" onChange={handleChange} className="hidden" />
                  </label>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-blue-gray-700 font-medium">Changer la photo</span>
                  <span className="text-xs text-blue-gray-400">JPG, PNG ou GIF (max. 2Mo)</span>
                </div>
              </div>
              <h2 className="font-heading text-xl mb-4">Informations personnelles</h2>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input label="Prénom" name="first_name" value={form.first_name} onChange={handleChange} autoComplete="given-name" />
                <Input label="Nom" name="last_name" value={form.last_name} onChange={handleChange} autoComplete="family-name" />
                <Input label="Email" name="email" value={form.email} readOnly autoComplete="email" />
                <Input label="Nouveau mot de passe" name="password" type="password" value={form.password} onChange={handleChange} autoComplete="new-password" />
                <Input label="Confirmer le mot de passe" name="password2" type="password" value={form.password2} onChange={handleChange} autoComplete="new-password" />
                {errors.password2 && <div className="text-error text-xs">{errors.password2}</div>}
                <Button type="submit" disabled={loading} className="w-full mt-2">{loading ? 'Enregistrement...' : 'Enregistrer'}</Button>
                {success && <div className="text-success text-sm text-center mb-2">Modifications enregistrées !</div>}
              </form>
            </Card>
            <Card>
              <h2 className="font-heading text-xl mb-4">Cache local</h2>
              <p className="text-sm text-blue-gray-500 mb-4">Vider le cache local (pages de référence, recommandations SEO, etc.) pour forcer le rechargement des données.</p>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  const n = clearAppCache();
                  showToast(n > 0 ? `Cache vidé (${n} entrée(s) supprimée(s)).` : 'Cache déjà vide.', 'success');
                }}
              >
                Vider le cache
              </Button>
            </Card>
          </div>
        )}
        {activeTab === 'apikeys' && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
            {/* Clé Gemini */}
            <div className="flex flex-col h-full min-h-0">
              <div className="flex gap-3 items-start mb-4">
                <span
                  className={`mt-0.5 h-7 w-7 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${geminiConfigured ? 'bg-green-500 text-white' : 'bg-accent text-white'}`}
                >
                  {geminiConfigured ? '✓' : '1'}
                </span>
                <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <h2 className="font-heading text-xl leading-tight">Clé Gemini</h2>
                    {nextCardToConfigure === 'gemini' && (
                      <span className="inline-flex items-center text-[11px] font-semibold leading-none text-amber-900 bg-amber-50 rounded-full px-2.5 py-1.5 ring-1 ring-amber-200/80 shadow-sm whitespace-nowrap">
                        À configurer en priorité
                      </span>
                    )}
                  </div>
                  <p className="text-blue-gray-500 text-sm min-h-[2.75rem] leading-snug">
                    IA pour l'analyse de contenu SEO et suggestions d'optimisation
                  </p>
                </div>
              </div>
              <Card
                className={`p-6 flex flex-col flex-1 w-full min-h-0 transition-shadow ${
                  nextCardToConfigure === 'gemini'
                    ? '!border-2 !border-accent rounded-xl bg-white shadow-lg shadow-accent/10'
                    : ''
                }`}
              >
              <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-2 mb-4">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-accent/10 to-blue-100">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-accent">
                    <circle cx="12" cy="12" r="10" fill="currentColor" opacity=".12" />
                    <path d="M12 7a5 5 0 100 10 5 5 0 000-10z" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M12 9.5v2.5l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="font-heading text-lg text-blue-gray-900 min-w-0 leading-snug">Google Gemini API</span>
                <span className="inline-flex items-center justify-center text-[11px] font-semibold bg-red-50 text-red-800 rounded-full px-2.5 py-1.5 ring-1 ring-inset ring-red-200/80 shadow-sm whitespace-nowrap self-center">Requis</span>
              </div>
              <div className="min-h-[6rem] mb-4 text-sm text-blue-gray-600 leading-relaxed">
                Une fois enregistrée, la clé est utilisée pour toutes les fonctionnalités IA de l’application.
              </div>
              <div className="grid grid-cols-[1fr_2.5rem] gap-2 items-center mb-4">
                <div className="relative min-w-0">
                  <input
                    type={geminiShow ? 'text' : 'password'}
                    value={geminiInput}
                    onChange={e => setGeminiInput(e.target.value)}
                    placeholder={!geminiInput ? 'Renseignez votre clé Gemini...' : ''}
                    className="h-10 w-full bg-white/60 border border-blue-gray-200 rounded-md px-3 text-sm font-mono placeholder-blue-gray-300 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition pr-10"
                    disabled={geminiLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-accent hover:text-accent-dark focus:outline-none"
                    onClick={() => setGeminiShow(s => !s)}
                    aria-label={geminiShow ? 'Masquer la clé' : 'Afficher la clé'}
                  >
                    {geminiShow ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" /><path d="M12 15a3 3 0 100-6 3 3 0 000 6z" /></svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-10 w-10 shrink-0 p-0 inline-flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 focus:ring-red-200"
                  onClick={handleGeminiDelete}
                  disabled={!geminiConfigured || geminiLoading}
                  aria-label="Supprimer la clé Gemini"
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="7" width="14" height="10" rx="2" stroke="currentColor" />
                    <path d="M9 10v4M12 10v4M15 10v4" stroke="currentColor" />
                    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" />
                    <line x1="3" y1="7" x2="21" y2="7" stroke="currentColor" />
                  </svg>
                </Button>
              </div>
              <Button type="button" className="w-full" onClick={handleGeminiSave} disabled={geminiLoading || !geminiInput}>{geminiConfigured ? 'Mettre à jour la clé' : 'Enregistrer la clé'}</Button>
              {geminiConfigured && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 mb-1">
                  <div className="flex items-center gap-2">
                    <Badge color="success" className="h-2 w-2 p-0 rounded-full" />
                    <span className="text-xs text-green-600">Configuré</span>
                  </div>
                  <a href="https://aistudio.google.com/usage" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-violet-600 hover:underline">
                    Voir l'usage et les quotas (compte Google) <ExternalLink size={12} />
                  </a>
                </div>
              )}

              {/* Consommation tokens Gemini — uniquement si une clé est enregistrée */}
              {geminiConfigured && geminiUsage && (
                <div className="mt-3 mb-1 border border-blue-gray-100 rounded-xl p-4 bg-blue-gray-50/50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-blue-gray-700">Consommation Gemini</span>
                    <button
                      type="button"
                      onClick={async () => {
                        setGeminiUsageResetting(true);
                        try { await resetGeminiUsage(); const d = await getGeminiUsage(); setGeminiUsage(d); showToast('Compteur remis à zéro', 'success'); }
                        catch { showToast('Erreur reset', 'error'); }
                        finally { setGeminiUsageResetting(false); }
                      }}
                      disabled={geminiUsageResetting}
                      className="text-xs text-red-500 hover:text-red-700 hover:underline transition-colors"
                    >
                      {geminiUsageResetting ? 'Reset...' : 'Remettre à zéro'}
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-white rounded-lg border border-blue-gray-100 px-3 py-2 text-center">
                      <div className="text-lg font-bold text-accent">{(geminiUsage.total_tokens || 0).toLocaleString('fr-FR')}</div>
                      <div className="text-xs text-blue-gray-400">Tokens totaux</div>
                    </div>
                    <div className="bg-white rounded-lg border border-blue-gray-100 px-3 py-2 text-center">
                      <div className="text-lg font-bold text-violet-600">{(geminiUsage.total_cost_eur || 0).toFixed(4)}€</div>
                      <div className="text-xs text-blue-gray-400">Coût estimé</div>
                    </div>
                    <div className="bg-white rounded-lg border border-blue-gray-100 px-3 py-2 text-center">
                      <div className="text-sm font-semibold text-blue-gray-700">{(geminiUsage.total_input || 0).toLocaleString('fr-FR')}</div>
                      <div className="text-xs text-blue-gray-400">Tokens input</div>
                    </div>
                    <div className="bg-white rounded-lg border border-blue-gray-100 px-3 py-2 text-center">
                      <div className="text-sm font-semibold text-blue-gray-700">{(geminiUsage.total_output || 0).toLocaleString('fr-FR')}</div>
                      <div className="text-xs text-blue-gray-400">Tokens output</div>
                    </div>
                  </div>
                  {geminiUsage.last_updated && (
                    <div className="text-xs text-blue-gray-300 mt-2 text-right">
                      Dernière mise à jour : {new Date(geminiUsage.last_updated).toLocaleString('fr-FR')}
                    </div>
                  )}
                  <p className="text-[10px] text-blue-gray-300 mt-2">Estimé sur base Gemini Flash : 0,075€/1M tokens input · 0,30€/1M tokens output</p>
                </div>
              )}
              <div className={`mt-4 border-t pt-4 ${!geminiConfigured && openDropdownGemini ? 'border-accent/20' : 'border-blue-gray-100'}`}>
                <button
                  type="button"
                  onClick={() => setOpenDropdownGemini(!openDropdownGemini)}
                  className={`flex items-center justify-between w-full text-left text-sm font-medium transition-colors ${!geminiConfigured ? 'text-accent hover:text-accent-dark' : 'text-blue-gray-700 hover:text-accent'}`}
                >
                  <span>{geminiConfigured ? 'Guide Gemini' : '📖 Comment obtenir ma clé Gemini ?'}</span>
                  {openDropdownGemini ? <ChevronUp size={18} className="text-blue-gray-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-blue-gray-400 flex-shrink-0" />}
                </button>
                {openDropdownGemini && (
                  <div className={`mt-3 text-sm text-blue-gray-600 space-y-2 ${!geminiConfigured ? 'bg-accent/5 rounded-lg p-3' : ''}`}>
                    <ol className="list-decimal pl-5 space-y-1.5">
                      <li>Rendez-vous sur <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noopener noreferrer" className="text-accent underline font-medium">Google AI Studio</a>.</li>
                      <li>Connectez-vous avec votre compte Google.</li>
                      <li>Cliquez sur <b>Create API Key</b> ou <b>Créer une clé API</b>.</li>
                      <li>Copiez la clé et collez-la dans le champ ci-dessus.</li>
                      <li>Gardez votre clé secrète et ne la partagez jamais.</li>
                    </ol>
                    <p className="text-xs text-blue-gray-400 mt-2">Documentation : <a href="https://ai.google.dev/gemini-api/docs/get-api-key" target="_blank" rel="noopener noreferrer" className="text-accent underline">obtenir une clé API</a>.</p>
                  </div>
                )}
              </div>
              {geminiConfigured && (
                <div className="mt-6">
                  <h3 className="font-heading text-md mb-2">Modèles disponibles (version de votre compte)</h3>
                  <p className="text-xs text-blue-gray-500 mb-2">Liste des modèles Gemini accessibles avec votre clé. L’appli utilise le plus performant en priorité.</p>
                  <Button type="button" variant="secondary" className="mb-3" onClick={handleFetchGeminiModels} disabled={geminiModelsLoading}>
                    {geminiModelsLoading ? 'Chargement...' : geminiModelsList ? 'Actualiser la liste' : 'Voir les modèles disponibles'}
                  </Button>
                  {geminiModelsList && geminiModelsList.length > 0 && (
                    <ul className="text-sm text-blue-gray-700 space-y-1 mb-4 pl-4 list-disc">
                      {geminiModelsList.map((m) => (
                        <li key={m} className="font-mono">{m}</li>
                      ))}
                    </ul>
                  )}
                  <h3 className="font-heading text-md mb-2 mt-6">Tester la clé Gemini</h3>
                  <p className="text-xs text-blue-gray-500 mb-2">Le test utilise la clé actuellement affichée dans le champ ci-dessus. Si vous venez de coller une nouvelle clé, le test la vérifiera sans l’enregistrer ; cliquez sur « Mettre à jour la clé » pour l’enregistrer.</p>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={geminiPrompt}
                      onChange={e => setGeminiPrompt(e.target.value)}
                      placeholder="Exemple : Donne-moi 3 conseils SEO pour mon site..."
                      className="flex-1 bg-white/60 border border-blue-gray-200 rounded-md px-3 py-2 text-sm placeholder-blue-gray-300 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition"
                      disabled={geminiTestLoading}
                    />
                    <Button type="button" onClick={handleGeminiTest} disabled={geminiTestLoading || !geminiInput?.trim()} className="px-4">{geminiTestLoading ? 'Test...' : 'Tester la clé Gemini'}</Button>
                  </div>
                  {geminiTestError && <div className="text-error text-xs mb-2">{geminiTestError}</div>}
                  {geminiTestLoading ? (
                    <Loader label="Analyse Gemini en cours..." className="my-6" />
                  ) : geminiTestResult || geminiTestError ? (
                    <Card className="mt-2 bg-blue-50/60">
                      <div className="text-xs text-blue-gray-400 mb-1">Réponse Gemini :</div>
                      <pre className="whitespace-pre-wrap text-sm text-blue-gray-800">{JSON.stringify(geminiTestResult, null, 2)}</pre>
                    </Card>
                  ) : null}
                </div>
              )}
              </Card>
            </div>

            {/* Clé Google PageSpeed */}
            <div className="flex flex-col h-full min-h-0">
              <div className="flex gap-3 items-start mb-4">
                <span
                  className={`mt-0.5 h-7 w-7 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${pagespeedConfigured ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'}`}
                >
                  {pagespeedConfigured ? '✓' : '2'}
                </span>
                <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <h2 className="font-heading text-xl leading-tight">Clé Google PageSpeed</h2>
                    {nextCardToConfigure === 'pagespeed' && (
                      <span className="inline-flex items-center text-[11px] font-semibold leading-none text-sky-900 bg-sky-50 rounded-full px-2.5 py-1.5 ring-1 ring-sky-200/80 shadow-sm whitespace-nowrap">
                        À configurer maintenant
                      </span>
                    )}
                  </div>
                  <p className="text-blue-gray-500 text-sm min-h-[2.75rem] leading-snug">
                    Clé API globale pour toutes les analyses de performance de vos projets
                  </p>
                </div>
              </div>
              <Card
                className={`p-6 flex flex-col flex-1 w-full min-h-0 transition-shadow ${
                  nextCardToConfigure === 'pagespeed'
                    ? '!border-2 !border-sky-500 rounded-xl bg-white shadow-lg shadow-sky-500/10'
                    : !pagespeedConfigured
                      ? 'border-sky-200/60'
                      : ''
                }`}
              >
              <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-2 mb-4">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-accent/10 to-blue-100">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="h-6 w-6 text-accent">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="font-heading text-lg text-blue-gray-900 min-w-0 leading-snug">Google PageSpeed Insights API</span>
                <span className="inline-flex items-center justify-center text-[11px] font-semibold bg-sky-50 text-sky-800 rounded-full px-2.5 py-1.5 ring-1 ring-inset ring-sky-200/80 shadow-sm whitespace-nowrap self-center">Recommandé</span>
              </div>
              <div className="min-h-[6rem] mb-4 text-sm text-blue-gray-600 leading-relaxed">
                Cette clé sera utilisée pour toutes les analyses de performance de tous vos projets.
                Si un projet a sa propre clé, elle sera utilisée en priorité pour ce projet uniquement.
              </div>
              <div className="grid grid-cols-[1fr_2.5rem] gap-2 items-center mb-4">
                <div className="relative min-w-0">
                  <input
                    type={pagespeedShow ? 'text' : 'password'}
                    value={pagespeedInput}
                    onChange={e => setPagespeedInput(e.target.value)}
                    placeholder={!pagespeedInput ? 'Renseignez votre clé Google PageSpeed...' : ''}
                    className="h-10 w-full bg-white/60 border border-blue-gray-200 rounded-md px-3 text-sm font-mono placeholder-blue-gray-300 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition pr-10"
                    disabled={pagespeedLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-accent hover:text-accent-dark focus:outline-none"
                    onClick={() => setPagespeedShow(s => !s)}
                    aria-label={pagespeedShow ? 'Masquer la clé' : 'Afficher la clé'}
                  >
                    {pagespeedShow ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" /><path d="M12 15a3 3 0 100-6 3 3 0 000 6z" /></svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-10 w-10 shrink-0 p-0 inline-flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 focus:ring-red-200"
                  onClick={handlePageSpeedDelete}
                  disabled={!pagespeedConfigured || pagespeedLoading}
                  aria-label="Supprimer la clé PageSpeed"
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="7" width="14" height="10" rx="2" stroke="currentColor" />
                    <path d="M9 10v4M12 10v4M15 10v4" stroke="currentColor" />
                    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" />
                    <line x1="3" y1="7" x2="21" y2="7" stroke="currentColor" />
                  </svg>
                </Button>
              </div>
              <Button type="button" className="w-full" onClick={handlePageSpeedSave} disabled={pagespeedLoading || !pagespeedInput}>
                {pagespeedConfigured ? 'Mettre à jour la clé' : 'Enregistrer la clé'}
              </Button>
              {pagespeedConfigured && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 mb-1">
                  <div className="flex items-center gap-2">
                    <Badge color="success" className="h-2 w-2 p-0 rounded-full" />
                    <span className="text-xs text-green-600">Configuré</span>
                  </div>
                  <a href="https://console.cloud.google.com/apis/api/pagespeedonline.googleapis.com/quotas" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:underline">
                    Voir l'usage et les quotas (compte Google) <ExternalLink size={12} />
                  </a>
                </div>
              )}
              <div className={`mt-4 border-t pt-4 ${!pagespeedConfigured && openDropdownPageSpeed ? 'border-blue-200/50' : 'border-blue-gray-100'}`}>
                <button
                  type="button"
                  onClick={() => setOpenDropdownPageSpeed(!openDropdownPageSpeed)}
                  className={`flex items-center justify-between w-full text-left text-sm font-medium transition-colors ${!pagespeedConfigured ? 'text-blue-600 hover:text-blue-800' : 'text-blue-gray-700 hover:text-accent'}`}
                >
                  <span>{pagespeedConfigured ? 'Comment obtenir une clé API Google PageSpeed Insights ?' : '📖 Comment obtenir ma clé PageSpeed ?'}</span>
                  {openDropdownPageSpeed ? <ChevronUp size={18} className="text-blue-gray-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-blue-gray-400 flex-shrink-0" />}
                </button>
                {openDropdownPageSpeed && (
                  <div className={`mt-3 text-sm text-blue-gray-600 ${!pagespeedConfigured ? 'bg-blue-50/60 rounded-lg p-3' : ''}`}>
                    <ol className="list-decimal pl-5 space-y-1.5">
                      <li>Rendez-vous sur <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer" className="text-accent underline font-medium">Google Cloud Console</a></li>
                      <li>Créez un projet ou sélectionnez un projet existant</li>
                      <li>Activez l'API &quot;PageSpeed Insights API&quot;</li>
                      <li>Créez une clé API dans &quot;Identifiants&quot;</li>
                      <li>Copiez la clé et collez-la ci-dessus</li>
                    </ol>
                  </div>
                )}
              </div>
              </Card>
            </div>

            {/* Clé Semrush */}
            <div className="flex flex-col h-full min-h-0">
              <div className="flex gap-3 items-start mb-4">
                <span
                  className={`mt-0.5 h-7 w-7 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 ${semrushConfigured ? 'bg-green-500 text-white' : 'bg-orange-400 text-white'}`}
                >
                  {semrushConfigured ? '✓' : '3'}
                </span>
                <div className="min-w-0 flex-1 flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <h2 className="font-heading text-xl leading-tight">Clé Semrush</h2>
                    {nextCardToConfigure === 'semrush' && (
                      <span className="inline-flex items-center text-[11px] font-semibold leading-none text-orange-900 bg-orange-50 rounded-full px-2.5 py-1.5 ring-1 ring-orange-200/80 shadow-sm whitespace-nowrap">
                        Dernière étape (optionnel)
                      </span>
                    )}
                  </div>
                  <p className="text-blue-gray-500 text-sm min-h-[2.75rem] leading-snug">
                    Données de trafic organique, mots-clés, backlinks et score d'autorité
                  </p>
                </div>
              </div>
              <Card
                className={`p-6 flex flex-col flex-1 w-full min-h-0 transition-shadow ${
                  nextCardToConfigure === 'semrush'
                    ? '!border-2 !border-orange-400 rounded-xl bg-white shadow-lg shadow-orange-400/10'
                    : ''
                }`}
              >
              <div className="grid grid-cols-[2.5rem_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-2 mb-4">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-orange-100 to-yellow-50">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-orange-500">
                    <path d="M3 3h18M3 9h18M3 15h10M3 21h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    <circle cx="20" cy="18" r="3" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M22.5 20.5L24 22" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="font-heading text-lg text-blue-gray-900 min-w-0 leading-snug">Semrush API</span>
                <span className="inline-flex items-center justify-center text-[11px] font-semibold bg-slate-50 text-slate-600 rounded-full px-2.5 py-1.5 ring-1 ring-inset ring-slate-200/80 shadow-sm whitespace-nowrap self-center">Optionnel</span>
              </div>
              <div className="min-h-[6rem] mb-4 text-sm text-blue-gray-600 leading-relaxed">
                Récupère le trafic organique estimé, les mots-clés positionnés, les backlinks et le Score d'Autorité (AS) de vos projets.
              </div>
              <div className="grid grid-cols-[1fr_2.5rem] gap-2 items-center mb-4">
                <div className="relative min-w-0">
                  <input
                    type={semrushShow ? 'text' : 'password'}
                    value={semrushInput}
                    onChange={e => setSemrushInput(e.target.value)}
                    placeholder={!semrushInput ? 'Renseignez votre clé Semrush...' : ''}
                    className="h-10 w-full bg-white/60 border border-blue-gray-200 rounded-md px-3 text-sm font-mono placeholder-blue-gray-300 focus:outline-none focus:border-accent focus:ring-1 focus:ring-accent transition pr-10"
                    disabled={semrushLoading}
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-accent hover:text-accent-dark focus:outline-none"
                    onClick={() => setSemrushShow(s => !s)}
                    aria-label={semrushShow ? 'Masquer la clé' : 'Afficher la clé'}
                  >
                    {semrushShow ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" /><path d="M12 15a3 3 0 100-6 3 3 0 000 6z" /></svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" /><circle cx="12" cy="12" r="3" /></svg>
                    )}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="h-10 w-10 shrink-0 p-0 inline-flex items-center justify-center border border-red-200 text-red-500 hover:bg-red-50 focus:ring-red-200"
                  onClick={handleSemrushDelete}
                  disabled={!semrushConfigured || semrushLoading}
                  aria-label="Supprimer la clé Semrush"
                >
                  <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="5" y="7" width="14" height="10" rx="2" stroke="currentColor" />
                    <path d="M9 10v4M12 10v4M15 10v4" stroke="currentColor" />
                    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" />
                    <line x1="3" y1="7" x2="21" y2="7" stroke="currentColor" />
                  </svg>
                </Button>
              </div>
              <Button type="button" className="w-full" onClick={handleSemrushSave} disabled={semrushLoading || !semrushInput}>
                {semrushConfigured ? 'Mettre à jour la clé' : 'Enregistrer la clé'}
              </Button>
              {semrushConfigured && (
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-2">
                  <div className="flex items-center gap-2">
                    <Badge color="success" className="h-2 w-2 p-0 rounded-full" />
                    <span className="text-xs text-green-600">Configuré</span>
                  </div>
                  {semrushCredits !== null && (
                    <div className="flex items-center gap-1.5 bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-accent">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      <span className="text-xs font-medium text-neutral-700">
                        <span className="font-bold text-accent">{semrushCredits.toLocaleString('fr-FR')}</span> crédits restants
                      </span>
                    </div>
                  )}
                </div>
              )}
              <div className={`mt-4 border-t pt-4 ${!semrushConfigured && openDropdownSemrush ? 'border-orange-200/50' : 'border-blue-gray-100'}`}>
                <button
                  type="button"
                  onClick={() => setOpenDropdownSemrush(!openDropdownSemrush)}
                  className={`flex items-center justify-between w-full text-left text-sm font-medium transition-colors ${!semrushConfigured ? 'text-orange-600 hover:text-orange-800' : 'text-blue-gray-700 hover:text-accent'}`}
                >
                  <span>{semrushConfigured ? 'Comment obtenir une clé API Semrush ?' : '📖 Comment obtenir ma clé Semrush ?'}</span>
                  {openDropdownSemrush ? <ChevronUp size={18} className="text-blue-gray-400 flex-shrink-0" /> : <ChevronDown size={18} className="text-blue-gray-400 flex-shrink-0" />}
                </button>
                {openDropdownSemrush && (
                  <div className={`mt-3 text-sm text-blue-gray-600 ${!semrushConfigured ? 'bg-orange-50/60 rounded-lg p-3' : ''}`}>
                    <ol className="list-decimal pl-5 space-y-1.5">
                      <li>Connectez-vous sur <a href="https://www.semrush.com/" target="_blank" rel="noopener noreferrer" className="text-accent underline font-medium">semrush.com</a></li>
                      <li>Allez dans <strong>Account &gt; Profile &gt; API</strong></li>
                      <li>Générez ou copiez votre clé API</li>
                      <li>Notez : chaque appel consomme ~2 unités API Semrush. Les crédits restants s’affichent ci-dessus.</li>
                    </ol>
                  </div>
                )}
              </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}